import {
  DayPartSections,
  GroupedSection,
  GroupSectionType,
  ID,
  SortingMode,
  Task,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { addDays, endOfDay, isAfter } from 'date-fns';

import { getSortedTasks } from './get-sorted-tasks';
import { getDueTaskPlanning } from './get-task-planning';
import { isNumber } from './is-number';
import { getActiveSchedule } from './schedules/get-active-schedule';

export type GroupTasksByDayPartLabels = Record<DayPartSections, string>;

const DEFAULT_LABELS: GroupTasksByDayPartLabels = {
  [DayPartSections.Overdue]: 'Overdue',
  [DayPartSections.AllDay]: 'All day',
  [DayPartSections.Morning]: 'Morning',
  [DayPartSections.Afternoon]: 'Afternoon',
  [DayPartSections.Evening]: 'Evening',
};

const SECTION_ORDER: DayPartSections[] = [
  DayPartSections.Overdue,
  DayPartSections.AllDay,
  DayPartSections.Morning,
  DayPartSections.Afternoon,
  DayPartSections.Evening,
];

const getTaskTimeslot = (
  task: Task,
  today: Timestamp,
  plannedOccurrence:
    | {
        date: Timestamp;
        timeslot?: {
          startTime?: number | null;
          duration?: number | null;
        } | null;
      }
    | undefined,
):
  | { startTime?: number | null; duration?: number | null }
  | null
  | undefined => {
  if (plannedOccurrence?.timeslot) {
    return plannedOccurrence.timeslot;
  }

  const schedule = getActiveSchedule(task.schedules, today);
  if (schedule?.timeslot) {
    return schedule.timeslot;
  }

  return task.timeslot;
};

const getDayPartSection = (
  timeslot:
    | { startTime?: number | null; duration?: number | null }
    | null
    | undefined,
): DayPartSections => {
  if (!isNumber(timeslot?.startTime)) {
    return DayPartSections.AllDay;
  }

  const date = new Date();
  date.setHours(0, 0, 0, timeslot!.startTime!);
  const startHour = date.getHours();

  if (startHour < 12) {
    return DayPartSections.Morning;
  }

  if (startHour >= 12 && startHour < 18) {
    return DayPartSections.Afternoon;
  }

  return DayPartSections.Evening;
};

export type GroupTasksByDayPartOptions = {
  tasks: Task[];
  today: Timestamp;
  weekStartsOn: WeekDays;
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  labels?: GroupTasksByDayPartLabels;
};

export const groupTasksByDayPart = ({
  tasks,
  today,
  weekStartsOn,
  customOrder,
  sortingMode,
  labels = DEFAULT_LABELS,
}: GroupTasksByDayPartOptions): GroupedSection[] => {
  const overdueDeadline = endOfDay(addDays(today, -1));

  const sectionTasks: Record<DayPartSections, Task[]> = {
    [DayPartSections.Overdue]: [],
    [DayPartSections.AllDay]: [],
    [DayPartSections.Morning]: [],
    [DayPartSections.Afternoon]: [],
    [DayPartSections.Evening]: [],
  };

  tasks.forEach((task) => {
    if (task.completedAt) {
      return;
    }

    const overduePlanning = getDueTaskPlanning(task, {
      date: overdueDeadline,
      today,
      weekStartsOn,
    });

    const duePlanning = overduePlanning.filter(
      (plannedOccurrence) => !isAfter(plannedOccurrence.date, overdueDeadline),
    );

    const overduePlannedOccurrence = duePlanning[duePlanning.length - 1];
    const isOverdue =
      !!overduePlannedOccurrence ||
      (!!task.deadlineDeadline &&
        !isAfter(task.deadlineDeadline, overdueDeadline));

    if (isOverdue) {
      sectionTasks[DayPartSections.Overdue].push(task);
      return;
    }

    const planning = getDueTaskPlanning(task, {
      date: today,
      today,
      weekStartsOn,
    });

    const firstOccurrence = planning[0];
    const timeslot = firstOccurrence
      ? getTaskTimeslot(task, today, firstOccurrence)
      : task.timeslot;

    const section = getDayPartSection(timeslot);
    sectionTasks[section].push(task);
  });

  return SECTION_ORDER.map((section) => {
    const orderForSection =
      sortingMode === SortingMode.Custom ? customOrder?.[section] ?? [] : [];

    const sortedTasks = getSortedTasks(sectionTasks[section], {
      today,
      weekStartsOn,
      order: orderForSection,
      mode:
        section === DayPartSections.Overdue
          ? [SortingMode.DueDate]
          : sortingMode
            ? [SortingMode.DueDate, sortingMode]
            : undefined,
      invertedTimeslots: section === DayPartSections.Overdue ? true : undefined,
    });

    return {
      sectionId: section,
      sectionType: GroupSectionType.DayPart,
      name: labels[section],
      tasks: sortedTasks,
    };
  });
};
